import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { excelParserService, type ExcelParseResult } from '../../../services/excelParserService';
import type { SchoolCreateRequest } from '../types';
import toast from 'react-hot-toast';
import { getSchoolLevelLabel } from '../constants';

interface ImportSchoolsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (schools: SchoolCreateRequest[]) => Promise<void>;
  isImporting: boolean;
}

const ImportSchoolsModal: React.FC<ImportSchoolsModalProps> = ({
  isOpen,
  onClose,
  onImport,
  isImporting,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ExcelParseResult | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'importing'>('upload');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];

    if (!validTypes.includes(file.type)) {
      toast.error('Vui lòng chọn file Excel (.xlsx, .xls) hoặc CSV');
      return;
    }

    setFile(file);
    setIsParsing(true);
    setCurrentStep('preview');

    try {
      const result = await excelParserService.parseExcelFile(file);
      setParseResult(result);
    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error('Lỗi khi đọc file. Vui lòng kiểm tra định dạng file.');
    } finally {
      setIsParsing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    multiple: false,
    disabled: isImporting || isParsing
  });

  const handleImport = async () => {
    if (!parseResult || !parseResult.success) return;

    setCurrentStep('importing');
    try {
      await onImport(parseResult.data);
      handleReset();
      onClose();
    } catch (error) {
      console.error('Import error:', error);
      // Error handling is done in parent component
    }
  };

  const handleReset = () => {
    setFile(null);
    setParseResult(null);
    setCurrentStep('upload');
    setIsParsing(false);
  };

  const handleClose = () => {
    if (!isImporting && !isParsing) {
      handleReset();
      onClose();
    }
  };

  const downloadTemplate = () => {
    const template = excelParserService.generateTemplate();
    const url = URL.createObjectURL(template);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_import_truong_hoc.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
          <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Import trường học từ Excel</h3>
        <p className="text-sm text-gray-600 mb-4">
          Kéo thả file Excel vào đây hoặc click để chọn file
        </p>
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${isImporting || isParsing ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="space-y-2">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm text-gray-600">
            {isDragActive ? 'Thả file vào đây...' : 'Chọn file Excel (.xlsx, .xls) hoặc CSV'}
          </p>
        </div>
      </div>

      <div className="text-center">
        <Button
          type="button"
          variant="outline"
          onClick={downloadTemplate}
          className="text-sm"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Tải template mẫu
        </Button>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    if (!parseResult) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Xem trước dữ liệu</h3>
          <div className="text-sm text-gray-500">
            File: {file?.name} ({parseResult.data.length} trường học)
          </div>
        </div>

        {/* Errors */}
        {parseResult.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-red-800 mb-2">
                  Lỗi ({parseResult.errors.length})
                </h4>
                <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                  {parseResult.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Warnings */}
        {parseResult.warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-yellow-800 mb-2">
                  Cảnh báo ({parseResult.warnings.length})
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {parseResult.warnings.map((warning, index) => (
                    <li key={index}>• {warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Success message */}
        {parseResult.success && parseResult.data.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-green-800">
                  Dữ liệu hợp lệ! Sẵn sàng import {parseResult.data.length} trường học.
                </h4>
              </div>
            </div>
          </div>
        )}

        {/* Data preview table */}
        {parseResult.data.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h4 className="text-sm font-medium text-gray-900">Dữ liệu sẽ được import</h4>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tên trường</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mã trường</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Loại</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cấp học</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tỉnh/Thành phố</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {parseResult.data.slice(0, 10).map((school, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2 text-sm text-gray-900 truncate max-w-32">{school.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{school.code}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{school.type}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{getSchoolLevelLabel(school.level)}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{school.city}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parseResult.data.length > 10 && (
                <div className="px-3 py-2 text-sm text-gray-500 text-center bg-gray-50">
                  ... và {parseResult.data.length - 10} trường học khác
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={isImporting}
          >
            Chọn file khác
          </Button>
          <div className="space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isImporting}
            >
              Hủy
            </Button>
            <Button
              type="button"
              onClick={handleImport}
              disabled={!parseResult.success || parseResult.data.length === 0 || isImporting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isImporting ? 'Đang import...' : `Import ${parseResult.data.length} trường học`}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderImportingStep = () => (
    <div className="text-center py-8">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Đang import dữ liệu...</h3>
      <p className="text-sm text-gray-600">Vui lòng chờ trong giây lát</p>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0">
        <div className="flex flex-col">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <DialogHeader className="space-y-1">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Import trường học từ Excel
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                Tải lên file Excel để import hàng loạt dữ liệu trường học
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {currentStep === 'upload' && renderUploadStep()}
            {currentStep === 'preview' && (isParsing ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Đang đọc file...</p>
              </div>
            ) : renderPreviewStep())}
            {currentStep === 'importing' && renderImportingStep()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportSchoolsModal;
